import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { RequestsPort } from '../../../app/ports/people/requests.port';
import { EnvMicroPeople } from '../../config/env.micro.people';
import { PEOPLE_ENV } from '../../config/micros-env.tokens';
import { SessionCtl } from '../../services/session.ctl.service';

type RequestsMockAction = 'getAll' | 'getById' | 'create' | 'update';

@Injectable({ providedIn: 'root' })
export class RequestsPortAdapter implements RequestsPort {
  constructor(
    private requestsApi: PeopleSdk.RequestsService,
    private http: HttpClient,
    private appEvents: AppEventsService,
    private sessionCtl: SessionCtl,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(PEOPLE_ENV) private env: EnvMicroPeople,
  ) {}

  getAllRequests(
    page?: number,
    size?: number,
    requestDate?: string,
    type?: PeopleSdk.RequestTypeEnum,
    status?: PeopleSdk.RequestStatusEnum,
  ): Observable<PeopleSdk.PagedRequestListResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-requests-list');

    const source$ = this.shouldMock('getAll')
      ? of({} as PeopleSdk.PagedRequestListResponse).pipe(delay(360))
      : this.requestsApi.getAllRequests(page, size, requestDate, type, status);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-requests-list')),
    );
  }

  getMyRequests(
    page?: number,
    size?: number,
    requestDate?: string,
    type?: PeopleSdk.RequestTypeEnum,
    status?: PeopleSdk.RequestStatusEnum,
  ): Observable<PeopleSdk.PagedRequestListResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-my-requests-list');

    const source$ = this.shouldMock('getAll')
      ? of({} as PeopleSdk.PagedRequestListResponse).pipe(delay(360))
      : this.http.get<PeopleSdk.PagedRequestListResponse>(
          `${this.env.host}${this.env.apiBasePath}/api/v1/requests/me`,
          {
            params: this.buildRequestParams(page, size, requestDate, type, status),
            headers: this.buildCurrentEmployeeHeaders(),
          },
        );

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-my-requests-list')),
    );
  }

  getRequestById(requestId: number): Observable<PeopleSdk.RequestResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-request-getById');

    const source$ = this.shouldMock('getById')
      ? of({} as PeopleSdk.RequestResponse).pipe(delay(360))
      : this.requestsApi.getRequestById(requestId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-request-getById')),
    );
  }

  createRequest(payload: PeopleSdk.RequestCreateRequest): Observable<PeopleSdk.RequestResponse> {
    this.appEvents.loaderVisiblePublish(true, 'people-request-create');

    const source$ = this.shouldMock('create')
      ? of({} as PeopleSdk.RequestResponse).pipe(delay(360))
      : this.requestsApi.createRequest(payload);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-request-create')),
    );
  }

  updateRequest(requestId: number, payload: PeopleSdk.RequestUpdateRequest): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-request-update');

    const source$ = this.shouldMock('update')
      ? of(null).pipe(delay(360))
      : this.requestsApi.updateRequest(requestId, payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-request-update')),
    );
  }

  private shouldMock(action: RequestsMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.requests?.[action]);
  }

  private buildRequestParams(
    page?: number,
    size?: number,
    requestDate?: string,
    type?: PeopleSdk.RequestTypeEnum,
    status?: PeopleSdk.RequestStatusEnum,
  ): HttpParams {
    let params = new HttpParams();
    if (page !== undefined && page !== null) params = params.set('page', String(page));
    if (size !== undefined && size !== null) params = params.set('size', String(size));
    if (requestDate) params = params.set('requestDate', requestDate);
    if (type) params = params.set('type', type);
    if (status) params = params.set('status', status);
    return params;
  }

  private buildCurrentEmployeeHeaders(): HttpHeaders {
    const employeeId = this.sessionCtl.getEmployeeId();
    return employeeId
      ? new HttpHeaders().set('X-Employee-Id', String(employeeId))
      : new HttpHeaders();
  }

  private showErrorToast(err: any) {
    if (!this.toast) return;

    const status = err?.status;
    const mapMsg: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    this.toast.show({
      data: {
        title: 'Error',
        msg: mapMsg[status] || 'Error inesperado',
        direction: CoreUIToastDirection.topCenter,
        autoClose: true,
      },
    });
  }
}
